/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { JSX } from 'solid-js';
import { Component, JSXElement, Show } from 'solid-js';
import { BtnClasses } from '../../types/common';

export interface PopupProps {
  show?: boolean;
  name?: string;
  heading?: string;
  headingStyleClass?: string;
  subHeading?: string;
  content?: string | JSXElement;
  overflowClass?: string;
  overflowStyle?: string;
  hideFooter?: boolean;
  hideClose?: boolean;
  /** Extra classes on the root `.reveal-overlay` (e.g. z-index utility). */
  overlayClass?: string;
  /** Dialog semantics for accessibility when used as a modal. */
  role?: JSX.IntrinsicElements['div']['role'];
  'aria-modal'?: boolean;
  'aria-labelledby'?: string;
  /** Clicking the dimmed backdrop calls `onClose` (same as footer secondary / close when wired). */
  closeOnBackdropClick?: boolean;
  /** When true, backdrop and close button do not dismiss (e.g. while a save is in flight). */
  backdropAndCloseDisabled?: boolean;
  primaryBtnLabel?: string;
  primaryBtnClass?: BtnClasses;
  primaryBtnDisabled?: boolean;
  secondaryBtnLabel?: string;
  secondaryBtnClass?: BtnClasses;
  secondaryBtnDisabled?: boolean;
  /**
   * Fired after the bubbling `popup-*` CustomEvent (for `tc-popup` / delegated listeners).
   * Prefer these for Solid parents instead of relying only on DOM events.
   */
  onPrimaryAction?(event: MouseEvent): void;
  onSecondaryAction?(event: MouseEvent): void;
  onClose?(event: MouseEvent): void;
}

const Popup: Component<PopupProps> = (props) => {
  // Reactive getters for props
  const getShow = () => props.show ?? false;
  const getName = () => props.name;
  const getHeading = () => props.heading ?? '';
  const getHeadingStyleClass = () => props.headingStyleClass;
  const getSubHeading = () => props.subHeading;
  const getContent = () => props.content;
  const getOverflowClass = () => props.overflowClass;
  const getOverflowStyle = () => props.overflowStyle;
  const getHideFooter = () => props.hideFooter ?? false;
  const getHideClose = () => props.hideClose ?? false;
  const getOverlayClass = () => props.overlayClass ?? '';
  const getRole = () => props.role;
  const getAriaModal = () => props['aria-modal'];
  const getAriaLabelledBy = () => props['aria-labelledby'];
  const getCloseOnBackdropClick = () => props.closeOnBackdropClick ?? false;
  const getBackdropAndCloseDisabled = () =>
    props.backdropAndCloseDisabled ?? false;
  const getPrimaryBtnLabel = () => props.primaryBtnLabel;
  const getPrimaryBtnClass = () => props.primaryBtnClass ?? 'primary';
  const getPrimaryBtnDisabled = () => props.primaryBtnDisabled ?? false;
  const getSecondaryBtnLabel = () => props.secondaryBtnLabel;
  const getSecondaryBtnClass = () =>
    props.secondaryBtnClass ?? 'link-secondary';
  const getSecondaryBtnDisabled = () => props.secondaryBtnDisabled ?? false;

  const dispatchPopupEvent = (
    event: MouseEvent,
    name: 'popup-close' | 'popup-primary-action' | 'popup-secondary-action'
  ): void => {
    const el = event.currentTarget as HTMLElement;
    const customEvent = new CustomEvent(name, {
      detail: { originalEvent: event },
      bubbles: true,
    });
    el.dispatchEvent(customEvent);
  };

  const handleClose = (event: MouseEvent) => {
    if (getBackdropAndCloseDisabled()) {
      return;
    }
    dispatchPopupEvent(event, 'popup-close');
    props.onClose?.(event);
  };

  const handlePrimaryClick = (event: MouseEvent) => {
    dispatchPopupEvent(event, 'popup-primary-action');
    props.onPrimaryAction?.(event);
  };

  const handleSecondaryClick = (event: MouseEvent) => {
    if (getSecondaryBtnDisabled()) {
      return;
    }
    dispatchPopupEvent(event, 'popup-secondary-action');
    props.onSecondaryAction?.(event);
  };

  const handleOverlayClick = (event: MouseEvent) => {
    if (!getCloseOnBackdropClick() || getBackdropAndCloseDisabled()) {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    dispatchPopupEvent(event, 'popup-close');
    props.onClose?.(event);
  };

  const handleRevealPanelClick = (event: MouseEvent) => {
    if (getCloseOnBackdropClick()) {
      event.stopPropagation();
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.stopPropagation();
  };

  const getIdProp = () => {
    const name = getName();
    return name ? { id: name } : {};
  };

  const getRevealClass = () => {
    const overflowClass = getOverflowClass();
    return `reveal ${overflowClass || ''}`.trim();
  };

  const getScrollSectionClass = () => {
    const overflowClass = getOverflowClass();
    return `scroll-section ${overflowClass || ''}`.trim();
  };

  const overlayClassNames = () =>
    ['reveal-overlay', 'd-block', getOverlayClass()].filter(Boolean).join(' ');

  const ariaModalProp = () => {
    const v = getAriaModal();
    return v === undefined ? {} : { 'aria-modal': v };
  };

  const roleProp = () => {
    const r = getRole();
    return r ? { role: r } : {};
  };

  const ariaLabelledByProp = () => {
    const id = getAriaLabelledBy();
    return id ? { 'aria-labelledby': id } : {};
  };

  return (
    <Show when={getShow()}>
      <div
        class={overlayClassNames()}
        {...getIdProp()}
        {...roleProp()}
        {...ariaModalProp()}
        {...ariaLabelledByProp()}
        onClick={handleOverlayClick}
        onDragOver={handleDragOver}
      >
        <div class="reveal-container">
          <div class={getRevealClass()} onClick={handleRevealPanelClick}>
            <header class="align-items-start">
              <div>
                <h2 class={getHeadingStyleClass()}>{getHeading()}</h2>
                <Show when={getSubHeading()}>
                  <p>{getSubHeading()}</p>
                </Show>
              </div>
              <Show when={!getHideClose()}>
                <button
                  class="button icon-medium tertiary icon-circle"
                  type="button"
                  onClick={handleClose}
                  title="Close"
                  disabled={getBackdropAndCloseDisabled()}
                >
                  <i class="icon-font tc-icon-content-close" />
                </button>
              </Show>
            </header>
            <div class={getScrollSectionClass()} style={getOverflowStyle()}>
              <Show when={getContent()} fallback={<slot />}>
                {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                <div class="mt-2">{getContent()}</div>
              </Show>
            </div>
            <Show when={!getHideFooter()}>
              <footer>
                <hr />
                <div class="footer-content">
                  <div class="wrap-buttons">
                    <Show when={getSecondaryBtnLabel()}>
                      <button
                        class={`button ${getSecondaryBtnClass()}`}
                        onClick={handleSecondaryClick}
                        disabled={getSecondaryBtnDisabled()}
                      >
                        {getSecondaryBtnLabel()}
                      </button>
                    </Show>
                    <Show when={getPrimaryBtnLabel()}>
                      <button
                        class={`button ${getPrimaryBtnClass()}`}
                        onClick={handlePrimaryClick}
                        disabled={getPrimaryBtnDisabled()}
                      >
                        {getPrimaryBtnLabel()}
                      </button>
                    </Show>
                  </div>
                </div>
              </footer>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
};

export default Popup;
